import MainNav from '../components/MainNav';
import MainFooter from '../components/MainFooter';
import { Card, CardContent } from '../components/ui/card';
import { Users, Heart } from 'lucide-react';

const TeamPage = () => {
    const teamMembers = [
        {
            name: 'Dr. Herman I. Pryor Jr., Ed.D.',
            role: 'Founder & Executive Director | Fundraising Committee Chair',
            bio: 'Visionary leader with expertise in counseling, psychology, and workforce transformation.',
            image: 'https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/dn2b2ve3_image.png'
        },
        {
            name: 'Ihkeem Ma\'at',
            role: 'IT/Marketing Committee Chair',
            bio: 'Leading our technology and marketing initiatives to expand the Recovery Room\'s digital presence and impact.',
            image: 'https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/pp180r4r_Ihkeem%20Ma%27at.jpg'
        },
        {
            name: 'Kmt Shockley',
            role: 'Community Engagement Committee Chair',
            bio: 'Building bridges with the broader community and driving outreach initiatives to spread awareness of our mission.',
            image: 'https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/hduike6u_Kmt%20Stokely.jpg'
        },
        {
            name: 'Reginald Ready',
            role: 'Recruitment Committee Chair',
            bio: 'Welcoming new brothers into the community and ensuring a meaningful onboarding experience.',
            image: 'https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/ovdtwl4d_Reginald%20Ready.jpg',
            imagePosition: 'center 70%'
        },
        {
            name: 'Orlance Shirley',
            role: 'Mentorship Committee Chair',
            bio: 'Facilitating meaningful mentor-mentee relationships through the Brother 2 Brother program.',
            image: 'https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/27gzy5jg_Orlance%20Shirley.jpg'
        },
        {
            name: 'Anthony Carter',
            role: 'Professional Development Committee Chair',
            bio: 'Creating opportunities for career growth, skill-building, and professional networking within our community.',
            image: 'https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/woy9r3oq_Anthony%20Carter.jpg'
        }
    ];

    const committees = [
        {
            name: 'Brother 2 Brother (Mentorship)',
            description: 'Facilitating meaningful mentor-mentee relationships between brothers in the community.'
        },
        {
            name: 'Fundraising Committee',
            description: 'Supporting the financial sustainability and growth of Recovery Room initiatives.'
        },
        {
            name: 'Community Engagement',
            description: 'Building bridges with the broader community and spreading awareness of our mission.'
        },
        {
            name: 'Professional Development',
            description: 'Creating opportunities for career growth, skill-building, and professional networking.'
        },
        {
            name: 'Recruitment Committee',
            description: 'Welcoming new brothers into the community and ensuring a smooth onboarding experience.'
        },
        {
            name: 'IT/Marketing Committee',
            description: 'Managing technology solutions, digital presence, and marketing strategies to amplify our reach and impact.'
        }
    ];

    return (
        <div className="min-h-screen bg-[#3D4435]">
            <MainNav />
            
            <main className="pt-32 pb-24 px-6">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-4">
                            Who We Are
                        </p>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Playfair_Display'] mb-6">
                            Our Team
                        </h1>
                        <p className="text-[#A0A0A0] text-lg max-w-2xl mx-auto">
                            Meet the dedicated individuals working to support Black men's wellness and community building
                        </p>
                    </div>

                    {/* Leadership */}
                    <section className="mb-20">
                        <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-8 flex items-center gap-3">
                            <Users className="w-6 h-6 text-[#C9A227]" />
                            Leadership
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {teamMembers.map((member, idx) => (
                                <Card key={idx} className="bg-[#4A5240] border-[#5A6350] overflow-hidden">
                                    <CardContent className="p-0">
                                        {member.image ? (
                                            <img 
                                                src={member.image} 
                                                alt={member.name}
                                                className="w-full h-64 object-cover"
                                                style={{ objectPosition: member.imagePosition || 'center top' }}
                                            />
                                        ) : (
                                            <div className="w-full h-64 bg-gradient-to-br from-[#5A6350] to-[#3D4435] flex items-center justify-center">
                                                <span className="text-6xl font-bold text-[#C9A227]">
                                                    {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                                </span>
                                            </div>
                                        )}
                                        <div className="p-6">
                                            <h3 className="text-white font-semibold text-lg">{member.name}</h3>
                                            <p className="text-[#C9A227] text-sm mb-3">{member.role}</p>
                                            <p className="text-[#A0A0A0] text-sm">{member.bio}</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </section>

                    {/* Committees */}
                    <section>
                        <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-8 flex items-center gap-3">
                            <Heart className="w-6 h-6 text-[#C9A227]" />
                            Our Committees
                        </h2>
                        <p className="text-[#A0A0A0] mb-8 max-w-3xl">
                            Our community is powered by dedicated volunteers who serve on various committees. Each committee plays a vital role in fulfilling our mission and creating meaningful experiences for our brothers.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {committees.map((committee, idx) => (
                                <Card key={idx} className="bg-[#4A5240] border-[#5A6350] hover:border-[#C9A227]/50 transition-all">
                                    <CardContent className="p-6">
                                        <h3 className="text-white font-semibold text-lg mb-2">{committee.name}</h3>
                                        <p className="text-[#A0A0A0] text-sm">{committee.description}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </section>

                    {/* Join the Team CTA */}
                    <section className="mt-20">
                        <Card className="bg-gradient-to-br from-[#C9A227]/20 to-[#C9A227]/5 border-[#C9A227]/30">
                            <CardContent className="p-8 text-center">
                                <h3 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-4">
                                    Interested in Joining a Committee?
                                </h3>
                                <p className="text-[#A0A0A0] mb-6 max-w-xl mx-auto">
                                    Become a member and indicate your committee interest in your profile. We're always looking for brothers who want to contribute to our mission.
                                </p>
                                <a 
                                    href="/member/register"
                                    className="inline-flex items-center px-6 py-3 bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold rounded-lg transition-colors"
                                >
                                    Become a Member
                                </a>
                            </CardContent>
                        </Card>
                    </section>
                </div>
            </main>

            <MainFooter />
        </div>
    );
};

export default TeamPage;
